import type { ReviewGuide } from "./guide";

export const layerStatuses = ["unseen", "in-progress", "done", "question", "problem", "stale"] as const;
export const regionStatuses = ["unseen", "presented", "stale"] as const;
export const reviewVerdicts = ["approve", "request-changes", "comment", "not-ready"] as const;

export type LayerStatus = (typeof layerStatuses)[number];
export type RegionStatus = (typeof regionStatuses)[number];
export type ReviewVerdict = (typeof reviewVerdicts)[number];

interface EventBase {
  id: string;
  sessionId: string;
  guideHeadSha: string;
  createdAt: string;
}

export type ReviewEvent =
  | (EventBase & { type: "layer-status"; layerId: string; status: LayerStatus })
  | (EventBase & { type: "region-status"; regionId: string; status: RegionStatus })
  | (EventBase & { type: "focus-stop-visited"; stopId: string })
  | (EventBase & {
      type: "classification-feedback";
      layerId: string;
      itemId?: string;
      message: string;
    })
  | (EventBase & {
      type: "replay-completed";
      durationMs: number;
      verdict: ReviewVerdict;
      summary?: string;
    });

export type ReviewEventInput = ReviewEvent extends infer Event
  ? Event extends EventBase
    ? Omit<Event, keyof EventBase>
    : never
  : never;

export interface ProgressProjection {
  layerStatuses: Record<string, LayerStatus>;
  regionStatuses: Record<string, RegionStatus>;
  focusStopStatuses: Record<string, "visited">;
  deepLayersComplete: number;
  totalDeepLayers: number;
  layersComplete: number;
  totalLayers: number;
  canComplete: boolean;
}

export function projectProgress(guide: ReviewGuide, events: ReviewEvent[]): ProgressProjection {
  const layerState = Object.fromEntries(
    guide.layers.map((layer) => [layer.id, "unseen" as LayerStatus]),
  );
  const regionState = Object.fromEntries(
    guide.codeMap.regions.map((region) => [region.id, "unseen" as RegionStatus]),
  );
  const focusStopState: Record<string, "visited"> = {};

  const orderedEvents = [...events].sort((a, b) =>
    a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt.localeCompare(b.createdAt),
  );
  for (const event of orderedEvents) {
    if (event.guideHeadSha !== guide.source.headSha) continue;
    if (event.type === "layer-status" && event.layerId in layerState) {
      layerState[event.layerId] = event.status;
    }
    if (event.type === "region-status" && event.regionId in regionState) {
      regionState[event.regionId] = event.status;
    }
    if (event.type === "focus-stop-visited") focusStopState[event.stopId] = "visited";
  }

  const effectiveLayerComplete = (layerId: string): boolean => {
    return ["done", "question", "problem"].includes(layerState[layerId] ?? "unseen");
  };

  const deepLayers = guide.layers.filter((layer) => layer.scrutiny === "deep");
  const layersComplete = guide.layers.filter((layer) => effectiveLayerComplete(layer.id)).length;
  const deepLayersComplete = deepLayers.filter((layer) => effectiveLayerComplete(layer.id)).length;

  return {
    layerStatuses: layerState,
    regionStatuses: regionState,
    focusStopStatuses: focusStopState,
    deepLayersComplete,
    totalDeepLayers: deepLayers.length,
    layersComplete,
    totalLayers: guide.layers.length,
    canComplete: layersComplete === guide.layers.length,
  };
}

export function makeReviewEvent(
  event: ReviewEventInput,
  context: Pick<EventBase, "sessionId" | "guideHeadSha">,
): ReviewEvent {
  return {
    ...event,
    id: crypto.randomUUID(),
    sessionId: context.sessionId,
    guideHeadSha: context.guideHeadSha,
    createdAt: new Date().toISOString(),
  } as unknown as ReviewEvent;
}

export function staleTargets(
  previous: ReviewGuide,
  next: ReviewGuide,
  previousContextHashes: Record<string, string>,
  nextContextHashes: Record<string, string>,
): { layers: Set<string> } {
  const previousRegionIds = new Set(previous.codeMap.regions.map((region) => region.id));
  const staleLayers = new Set<string>();

  for (const layer of next.layers) {
    const changed = layer.items.some((item) =>
      item.regionIds.some(
        (regionId) =>
          !previousRegionIds.has(regionId) ||
          !previousContextHashes[regionId] ||
          previousContextHashes[regionId] !== nextContextHashes[regionId],
      ),
    );
    if (changed) {
      staleLayers.add(layer.id);
    }
  }
  return { layers: staleLayers };
}
